import {FC, useState} from 'react'
import {useAuth} from '../../modules/auth'
import {api, ApiError} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {PageHead} from './ui'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid #e5e7eb',
  borderRadius: 9,
  fontSize: 14,
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
}

const AccordionItem: FC<{title: string; open: boolean; onToggle: () => void; children: React.ReactNode}> = ({title, open, onToggle, children}) => (
  <div style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', border: '1px solid #eef0f4', marginBottom: 16, overflow: 'hidden'}}>
    <button
      onClick={onToggle}
      aria-expanded={open}
      style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '20px 26px', background: open ? 'rgba(3,149,64,0.04)' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left'}}
    >
      <span style={{fontSize: 18, fontWeight: 800, color: INK}}>{title}</span>
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke={open ? ACCENT : '#94a3b8'} strokeWidth='2.5' style={{flexShrink: 0, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none'}}>
        <polyline points='6 9 12 15 18 9' />
      </svg>
    </button>
    {open && <div style={{padding: '6px 26px 26px'}}>{children}</div>}
  </div>
)

const Notice: FC<{kind: 'ok' | 'err'; children: React.ReactNode}> = ({kind, children}) => (
  <div style={{borderRadius: 9, padding: '10px 14px', fontSize: 14, marginBottom: 16, ...(kind === 'ok' ? {background: 'rgba(3,149,64,0.1)', color: '#1f9d5b'} : {background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626'})}}>
    {children}
  </div>
)

const Label: FC<{children: React.ReactNode}> = ({children}) => (
  <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>{children}</label>
)

const ProfilePage: FC = () => {
  const {currentUser, setCurrentUser} = useAuth()

  // Single-open accordion — "Profile details" open by default.
  const [open, setOpen] = useState<'profile' | 'password' | ''>('profile')
  const toggle = (id: 'profile' | 'password') => setOpen((cur) => (cur === id ? '' : id))

  // Profile form
  const [firstName, setFirstName] = useState(currentUser?.first_name ?? '')
  const [lastName, setLastName] = useState(currentUser?.last_name ?? '')
  const [phone, setPhone] = useState(currentUser?.phone ?? '')
  const [pSaving, setPSaving] = useState(false)
  const [pMsg, setPMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)

  // Password form
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMsg, setPwMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)

  const saveProfile = async () => {
    setPSaving(true)
    setPMsg(null)
    try {
      const updated = await api.updateMyProfile({firstName, lastName, phone: phone.trim() || null})
      setCurrentUser((u) => (u ? {...u, first_name: updated.firstName, last_name: updated.lastName, phone: updated.phone ?? undefined} : u))
      setPMsg({kind: 'ok', text: 'Profile updated.'})
    } catch (e) {
      setPMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not save profile'})
    } finally {
      setPSaving(false)
    }
  }

  const changePassword = async () => {
    setPwMsg(null)
    if (next.length < 8) return setPwMsg({kind: 'err', text: 'New password must be at least 8 characters.'})
    if (next !== confirm) return setPwMsg({kind: 'err', text: 'New passwords do not match.'})
    setPwSaving(true)
    try {
      await api.changeMyPassword(current, next)
      setCurrent('')
      setNext('')
      setConfirm('')
      setPwMsg({kind: 'ok', text: 'Password changed.'})
    } catch (e) {
      setPwMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not change password'})
    } finally {
      setPwSaving(false)
    }
  }

  return (
    <div>
      <PageHead icon='👤' eyebrow='Account' title='Your profile' sub='Manage your details and password.' />

      <div className='ccp-stagger'>
      <AccordionItem title='Profile details' open={open === 'profile'} onToggle={() => toggle('profile')}>
        {pMsg && <Notice kind={pMsg.kind}>{pMsg.text}</Notice>}
        <div className='row g-3'>
          <div className='col-md-6'>
            <Label>First name</Label>
            <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className='col-md-6'>
            <Label>Last name</Label>
            <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div className='col-md-6'>
            <Label>Email</Label>
            <input style={{...inputStyle, background: '#f4f6fa', color: MUTED}} value={currentUser?.email ?? ''} disabled />
          </div>
          <div className='col-md-6'>
            <Label>Phone</Label>
            <input style={inputStyle} value={phone} placeholder='Optional' onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        <div style={{marginTop: 20}}>
          <button
            onClick={saveProfile}
            disabled={pSaving || !firstName.trim() || !lastName.trim()}
            style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 26px', cursor: 'pointer'}}
          >
            {pSaving ? <Spinner size={14} /> : 'Save changes'}
          </button>
        </div>
      </AccordionItem>

      <AccordionItem title='Change password' open={open === 'password'} onToggle={() => toggle('password')}>
        {pwMsg && <Notice kind={pwMsg.kind}>{pwMsg.text}</Notice>}
        <div className='row g-3'>
          <div className='col-md-4'>
            <Label>Current password</Label>
            <input type='password' style={inputStyle} value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className='col-md-4'>
            <Label>New password</Label>
            <input type='password' style={inputStyle} value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <div className='col-md-4'>
            <Label>Confirm new</Label>
            <input type='password' style={inputStyle} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
        </div>
        <div style={{marginTop: 20}}>
          <button
            onClick={changePassword}
            disabled={pwSaving || !current || !next || !confirm}
            style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 26px', cursor: 'pointer'}}
          >
            {pwSaving ? <Spinner size={14} /> : 'Update password'}
          </button>
        </div>
      </AccordionItem>
      </div>
    </div>
  )
}

export {ProfilePage}
